import { Link, useNavigation, useSearchParams } from '@remix-run/react';
import { useStyles } from '@archipro-design/aria';
import * as S from './FeaturedProducts.mobile.style';
import ProductTile from '~/modules/tile/component/product-tile/ProductTile';
import type ProductTileItem from '~/modules/tile/type/product-tile-item';
import React, { useState } from 'react';

import FeaturedItemsMobile from '~/modules/detail/components/featured-items/FeaturedItems.mobile';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
interface ProjectProductTile extends ProductTileItem {
    Professional: {
        ID: number;
        Title: string;
        Link: string;
    };
}

interface FeaturedProductsData {
    Title: string;
    FeaturedProducts: {
        pageInfo: {
            totalCount: number;
        };
        edges: {
            node: ProjectProductTile;
        }[];
    };
}

interface FeaturedProductsProps {
    data: FeaturedProductsData;
}

const ROW_SIZE = 2;
const DEFAULT_TILE_LIMIT = ROW_SIZE * 2;
const SHOW_ALL_PARAM = 'featured-products-show-all';

const FeaturedProductsMobile = ({
    data: { FeaturedProducts },
}: FeaturedProductsProps) => {
    const { css } = useStyles();
    const [searchParams] = useSearchParams();
    const [showing, setShowing] = useState(false);
    const navigation = useNavigation();
    const { favouriteItem } = useSaveToDesignBoard('ProductPin');

    const { edges, pageInfo } = FeaturedProducts;
    if (!edges?.length) return null;

    const totalCount = pageInfo.totalCount ?? 0;

    const showingAll = !!searchParams.get(SHOW_ALL_PARAM) || showing;
    const nextSearchParams = new URLSearchParams(navigation.location?.search);
    const featuredShowAll = nextSearchParams.get(SHOW_ALL_PARAM) === '1';
    const isLoading = navigation.state === 'loading' && featuredShowAll;

    return (
        <div className={css(S.ProductsUsedWrapper)}>
            <FeaturedItemsMobile
                gridType="double"
                title={
                    <>
                        Products used in <br /> this project
                    </>
                }
                displayButton={!showingAll && totalCount > DEFAULT_TILE_LIMIT}
                button={{
                    as: Link,
                    to: `?${SHOW_ALL_PARAM}=1`,
                    loading: isLoading,
                    onClick: () => {
                        if (totalCount <= DEFAULT_TILE_LIMIT) {
                            setShowing(true);
                        }
                    },
                    children: !isLoading ? `View more` : 'Loading...',
                }}
            >
                {edges
                    .slice(0, showingAll ? undefined : DEFAULT_TILE_LIMIT)
                    .map(({ node: product }, idx) => {
                        return (
                            <ProductTile
                                key={'product-' + idx}
                                data={{
                                    Product: product,
                                    Professional: product.Professional,
                                }}
                                size="medium"
                                favouriteItem={favouriteItem}
                                variant="02"
                            />
                        );
                    })}
            </FeaturedItemsMobile>
            {favouriteItem.target && <SaveToDesignBoard {...favouriteItem} />}
        </div>
    );
};

export default FeaturedProductsMobile;
